import { useState, useCallback } from "react";
import { Upload } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { documentsApi } from "@/api/documents";
import type { DocumentCategory } from "@/types/api";

const CATEGORIES: DocumentCategory[] = [
  "Application",
  "DealSheet",
  "FinancialStatement",
  "Appraisal",
  "TitleWork",
  "LegalDocument",
  "InsuranceCertificate",
  "Correspondence",
  "CreditReport",
  "ClosingDocument",
  "ComplianceDocument",
  "InvestorReport",
  "VendorAgreement",
  "BackgroundCheck",
  "Invoice",
  "TaxDocument",
  "ServiceReport",
  "Signature",
  "Photo",
  "Identification",
  "Other",
];

interface DocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  targetFolderId: string | null;
  targetFolderName?: string;
  onUploaded?: () => void;
}

interface StagedFile {
  file: File;
  title: string;
  category: DocumentCategory;
  tags: string;
  description: string;
  status: "pending" | "uploading" | "done" | "error";
  error?: string;
}

export function DocumentUploadModal({
  open,
  onClose,
  targetFolderId,
  targetFolderName,
  onUploaded,
}: DocumentUploadModalProps) {
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [autoIngest, setAutoIngest] = useState(true);
  const [bulkCategory, setBulkCategory] = useState<DocumentCategory>("Other");
  const [uploading, setUploading] = useState(false);

  const onFiles = useCallback((files: FileList | File[]) => {
    const next: StagedFile[] = Array.from(files).map((f) => ({
      file: f,
      title: stripExt(f.name),
      category: bulkCategory,
      tags: "",
      description: "",
      status: "pending",
    }));
    setStaged((prev) => [...prev, ...next]);
  }, [bulkCategory]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
  };

  const applyBulkCategory = (cat: DocumentCategory) => {
    setBulkCategory(cat);
    setStaged((prev) =>
      prev.map((s) => (s.status === "pending" ? { ...s, category: cat } : s)),
    );
  };

  const removeStaged = (i: number) =>
    setStaged((prev) => prev.filter((_, idx) => idx !== i));

  const updateStaged = (i: number, patch: Partial<StagedFile>) =>
    setStaged((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const handleUpload = async () => {
    if (!targetFolderId) return;
    setUploading(true);
    for (let i = 0; i < staged.length; i++) {
      const item = staged[i];
      if (item.status === "done") continue;
      updateStaged(i, { status: "uploading" });
      try {
        await documentsApi.upload({
          title: item.title.trim() || item.file.name,
          category: item.category,
          folderId: targetFolderId,
          description: item.description || undefined,
          tags: item.tags || undefined,
          file: item.file,
          autoIngest,
        });
        updateStaged(i, { status: "done" });
      } catch (err) {
        updateStaged(i, {
          status: "error",
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    setUploading(false);
    onUploaded?.();
  };

  const handleDone = () => {
    setStaged([]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleDone}
      dismissOnBackdropClick={false}
      title="Upload documents"
      description={targetFolderName ? `Destination: ${targetFolderName}` : undefined}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleDone} disabled={uploading}>
            {staged.every((s) => s.status === "done") ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!targetFolderId || staged.length === 0 || uploading}
          >
            {uploading ? "Uploading…" : `Upload ${staged.length} file(s)`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="border-[2px] border-dashed border-border-light rounded-md p-space-5 text-center bg-warm-white"
        >
          <Upload size={28} aria-hidden className="mx-auto text-gold mb-space-2" />
          <p className="text-small text-onyx">
            Drag files here, or{" "}
            <label className="underline text-gold-shadow cursor-pointer">
              browse
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => e.target.files && onFiles(e.target.files)}
              />
            </label>
          </p>
          <p className="text-caption text-gray-mid mt-space-1">
            PDF, DOCX, XLSX, PPTX, CSV, TXT, PNG, JPG, GIF, TIFF, SVG, MSG, EML
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-space-3">
          <FormField label="Bulk category" htmlFor="bulk-cat">
            <select
              id="bulk-cat"
              className="h-10 w-64 px-space-3 border border-border-light rounded-md bg-white-pure text-body"
              value={bulkCategory}
              onChange={(e) => applyBulkCategory(e.target.value as DocumentCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </FormField>
          <label className="flex items-center gap-space-2 text-small text-onyx h-10">
            <input
              type="checkbox"
              checked={autoIngest}
              onChange={(e) => setAutoIngest(e.target.checked)}
            />
            Auto-ingest after upload (LLM)
          </label>
        </div>

        {staged.length > 0 && (
          <div className="flex flex-col gap-space-2">
            {staged.map((s, i) => (
              <div
                key={i}
                className={`flex flex-col md:flex-row md:items-end gap-space-2 p-space-3 border rounded-md bg-white-pure ${
                  s.status === "error" ? "border-status-error" : "border-border-light"
                }`}
              >
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-space-2">
                  <FormField label="Title" htmlFor={`title-${i}`}>
                    <Input
                      id={`title-${i}`}
                      value={s.title}
                      onChange={(e) => updateStaged(i, { title: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Category" htmlFor={`cat-${i}`}>
                    <select
                      id={`cat-${i}`}
                      className="h-10 w-full px-space-3 border border-border-light rounded-md bg-white-pure text-body"
                      value={s.category}
                      onChange={(e) =>
                        updateStaged(i, {
                          category: e.target.value as DocumentCategory,
                        })
                      }
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label="Tags" htmlFor={`tags-${i}`}>
                    <Input
                      id={`tags-${i}`}
                      placeholder="comma, separated"
                      value={s.tags}
                      onChange={(e) => updateStaged(i, { tags: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Description" htmlFor={`desc-${i}`}>
                    <Textarea
                      id={`desc-${i}`}
                      rows={1}
                      value={s.description}
                      onChange={(e) => updateStaged(i, { description: e.target.value })}
                    />
                  </FormField>
                </div>
                <div className="flex flex-col items-end gap-space-1 min-w-[180px] max-w-[260px]">
                  <span className="text-caption uppercase tracking-wide text-gray-mid">
                    {s.file.name} ({formatBytes(s.file.size)})
                  </span>
                  <StatusPill status={s.status} error={s.error} />
                  {s.status === "error" && s.error && (
                    <p className="text-caption text-status-error break-words text-right">
                      {s.error}
                    </p>
                  )}
                  <Button variant="tertiary" size="sm" onClick={() => removeStaged(i)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function stripExt(name: string) {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function StatusPill({
  status,
  error,
}: {
  status: StagedFile["status"];
  error?: string;
}) {
  if (status === "pending") return <span className="text-caption text-gray-mid">Ready</span>;
  if (status === "uploading") return <span className="text-caption text-gold-shadow">Uploading…</span>;
  if (status === "done") return <span className="text-caption text-status-success">Done</span>;
  return (
    <span className="text-caption text-status-error" title={error}>
      Failed
    </span>
  );
}
